import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getStoredAuthUser, hasStoredAuthSession } from '../utils/authStorage';
import { Spinner, Container } from 'react-bootstrap';

const ProtectedRoute = ({ children, requiredRole }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();
  const storedUser = getStoredAuthUser();
  const sessionActive = isAuthenticated || hasStoredAuthSession();
  const effectiveUser = user || storedUser;

  // Role mapping for consistent comparison - matches backend user model
  const ROLE_MAP = {
    0: 'admin',        // Super Admin
    1: 'user',         // User
    2: 'organization', // Organization Admin
    // role > 2 → 'instructor' (handled in normalizeRole)
    'admin': 0,
    'user': 1,
    'organization': 2
  };

  // Normalize role to string for comparison
  const normalizeRole = (role) => {
    const numRole = typeof role === 'string' ? parseInt(role, 10) : role;
    if (typeof numRole === 'number' && !Number.isNaN(numRole)) {
      if (numRole > 2) return 'instructor';
      return ROLE_MAP[numRole] || 'unknown';
    }
    if (typeof role === 'string') return role.toLowerCase();
    return 'unknown';
  };

  // Get user's normalized role
  const userRole = normalizeRole(effectiveUser?.role);

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="text-center">
          <Spinner animation="border" role="status" variant="primary" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <div className="mt-3">
            <p className="text-muted">Checking authentication...</p>
          </div>
        </div>
      </Container>
    );
  }

  // Redirect to login if not authenticated
  if (!sessionActive) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role-based access if requiredRole is specified
  if (requiredRole && userRole !== requiredRole) {
    // Redirect to appropriate dashboard based on user's actual role
    switch (userRole) {
      case 'admin':
        return <Navigate to="/admin/dashboard" replace />;
      case 'user':
        return <Navigate to="/user/dashboard" replace />;
      case 'organization':
        return <Navigate to="/organization/dashboard" replace />;
      case 'instructor':
        return <Navigate to="/instructor/dashboard" replace />;
      default:
        return <Navigate to="/" replace />;
    }
  }

  // Render children if authenticated and authorized
  return children;
};

export default ProtectedRoute; 